import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useI18n } from "@/lib/i18n";
import api from "@/lib/api";
import { ArrowLeft, Edit2, Play, Trash2, ChevronRight } from "lucide-react";
import ProviderIcon from "@/components/ProviderIcon";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

function formatBytes(b) {
  if (!b) return "0 B";
  const u = ["B", "KB", "MB", "GB", "TB"];
  let i = 0;
  let n = b;
  while (n >= 1024 && i < u.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
}

function duration(a, b) {
  if (!a || !b) return "—";
  const ms = new Date(b).getTime() - new Date(a).getTime();
  if (ms < 1000) return `${ms}ms`;
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${s % 60}s`;
}

function StatusBadge({ status }) {
  const map = {
    idle: "bg-muted text-muted-foreground",
    running: "bg-blue-500/15 text-blue-600 dark:text-blue-400",
    success: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400",
    partial: "bg-amber-500/15 text-amber-700 dark:text-amber-400",
    failed: "bg-destructive/15 text-destructive",
  };
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-sm px-2 py-0.5 font-mono text-[10px] ${map[status] || map.idle}`}>
      <span className={`h-1.5 w-1.5 ${status === "running" ? "animate-pulse" : ""} bg-current`} />
      {status.toUpperCase()}
    </span>
  );
}

export default function JobDetail() {
  const { id } = useParams();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [runs, setRuns] = useState([]);
  const [runsTotal, setRunsTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [activeRun, setActiveRun] = useState(null);
  const [activeRunData, setActiveRunData] = useState(null);
  const [confirmDel, setConfirmDel] = useState(false);

  const PAGE_SIZE = 10;

  const load = useCallback(async () => {
    try {
      const [j, r] = await Promise.all([
        api.get(`/jobs/${id}`),
        api.get(`/jobs/${id}/runs`, { params: { skip: page * PAGE_SIZE, limit: PAGE_SIZE } }),
      ]);
      setJob(j.data);
      setRuns(r.data.items || []);
      setRunsTotal(r.data.total || 0);
      if ((r.data.items || []).length && !activeRun) {
        setActiveRun(r.data.items[0].id);
      }
    } catch (e) {
      if (e?.response?.status === 404) navigate("/dashboard");
    }
  }, [id, navigate, activeRun, page]);

  useEffect(() => {
    load();
    const interval = setInterval(load, 4000);
    return () => clearInterval(interval);
  }, [load]);

  useEffect(() => {
    if (!activeRun) {
      setActiveRunData(null);
      return;
    }
    const fetchRun = () => {
      api.get(`/jobs/${id}/runs/${activeRun}`).then(({ data }) => setActiveRunData(data));
    };
    fetchRun();
    const interval = setInterval(fetchRun, 3000);
    return () => clearInterval(interval);
  }, [id, activeRun]);

  const runNow = async () => {
    try {
      const { data } = await api.post(`/jobs/${id}/run`);
      toast.success("Backup started");
      setActiveRun(data.run_id);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/jobs/${id}`);
      toast.success("Deleted");
      navigate("/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  if (!job) {
    return (
      <div className="App min-h-screen bg-background">
        <Header variant="app" />
        <div className="mx-auto max-w-5xl px-4 py-10 font-mono text-sm text-muted-foreground">{t("common.loading")}</div>
      </div>
    );
  }

  return (
    <div className="App min-h-screen bg-background">
      <Header variant="app" />
      <main className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8">
        <button
          onClick={() => navigate(-1)}
          className="mb-4 inline-flex items-center gap-1 font-mono text-xs text-muted-foreground hover:text-foreground"
          data-testid="job-back"
        >
          <ArrowLeft className="h-3 w-3" /> {t("common.back")}
        </button>
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 font-mono text-xs text-muted-foreground">
              <span>job::{job.id.slice(0, 8)}</span>
              <StatusBadge status={job.status} />
              {!job.enabled && <Badge variant="outline" className="rounded-sm font-mono text-[10px]">{t("common.disabled")}</Badge>}
            </div>
            <h1 className="mt-2 font-display text-3xl font-black tracking-tight sm:text-4xl">{job.name}</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" className="rounded-sm gap-2" asChild data-testid="edit-job">
              <Link to={`/jobs/${id}/edit`}>
                <Edit2 className="h-4 w-4" /> {t("common.edit")}
              </Link>
            </Button>
            <Button onClick={runNow} className="rounded-sm bg-[#002FA7] hover:bg-[#002FA7]/90 text-white gap-2" data-testid="job-detail-run">
              <Play className="h-4 w-4" /> {t("dashboard.runNow")}
            </Button>
            <Button variant="outline" onClick={() => setConfirmDel(true)} className="rounded-sm gap-2 text-destructive border-destructive/30 hover:bg-destructive/10" data-testid="delete-job">
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Summary */}
        <section className="mt-8 grid grid-cols-1 gap-4 lg:grid-cols-3">
          <SummaryCard title={t("jobDetail.sourceLabel")} body={
            <>
              <div className="font-mono text-xs text-muted-foreground">account</div>
              <AccountLine provider={job.source_account_provider} email={job.source_account_email} fallback={job.source_account_id} />
              <div className="mt-2 font-mono text-xs text-muted-foreground">folder</div>
              <div className="truncate text-sm">{job.source_folder_name || "/"}</div>
            </>
          } />
          <SummaryCard title={t("jobDetail.destLabel")} body={
            <>
              <div className="font-mono text-xs text-muted-foreground">account</div>
              <AccountLine provider={job.dest_account_provider} email={job.dest_account_email} fallback={job.dest_account_id} />
              <div className="mt-2 font-mono text-xs text-muted-foreground">folder</div>
              <div className="truncate text-sm">{job.dest_folder_name || "/"}</div>
            </>
          } />
          <SummaryCard title={t("jobDetail.scheduleLabel")} body={
            <>
              <div className="font-mono text-xs text-muted-foreground">frequency</div>
              <div className="text-sm">{(job.schedule?.frequency || "manual").toUpperCase()}</div>
              <div className="mt-2 font-mono text-xs text-muted-foreground">next run</div>
              <div className="text-sm">
                {job.next_run_at ? new Date(job.next_run_at).toLocaleString() : t("dashboard.onDemand")}
              </div>
            </>
          } />
        </section>

        {/* Filters / options chips */}
        <section className="mt-6 flex flex-wrap items-center gap-2">
          {job.incremental && <Chip label="incremental" />}
          {job.versioning && <Chip label="versioning" />}
          {job.filters?.include_extensions?.length > 0 && (
            <Chip label={`include: ${job.filters.include_extensions.join(",")}`} />
          )}
          {job.filters?.exclude_extensions?.length > 0 && (
            <Chip label={`exclude: ${job.filters.exclude_extensions.join(",")}`} />
          )}
          {job.filters?.max_size_mb && <Chip label={`max ${job.filters.max_size_mb}MB`} />}
          {job.filters?.exclude_folder_names?.length > 0 && (
            <Chip label={`skip dirs: ${job.filters.exclude_folder_names.join(",")}`} />
          )}
        </section>

        {/* Runs + logs */}
        <section className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <div className="flex items-baseline justify-between">
              <h2 className="font-display text-xl font-bold tracking-tight">{t("jobDetail.historyTitle")}</h2>
              {runsTotal > 0 && (
                <span className="font-mono text-[11px] text-muted-foreground">
                  {Math.min(page * PAGE_SIZE + 1, runsTotal)}–{Math.min((page + 1) * PAGE_SIZE, runsTotal)} / {runsTotal}
                </span>
              )}
            </div>
            <div className="mt-4 border border-border bg-card">
              {runs.length === 0 && (
                <div className="p-6 font-mono text-sm text-muted-foreground">{t("jobDetail.noRuns")}</div>
              )}
              {runs.map((r) => (
                <button
                  key={r.id}
                  onClick={() => setActiveRun(r.id)}
                  className={`flex w-full items-center justify-between border-b border-border px-4 py-3 text-left text-sm hover:bg-accent/40 last:border-0 ${
                    activeRun === r.id ? "bg-accent/30" : ""
                  }`}
                  data-testid={`run-${r.id}`}
                >
                  <div>
                    <div className="flex items-center gap-2">
                      <StatusBadge status={r.status} />
                      <span className="font-mono text-xs text-muted-foreground">
                        {new Date(r.started_at).toLocaleString()}
                      </span>
                    </div>
                    <div className="mt-1 font-mono text-[11px] text-muted-foreground">
                      {r.files_copied}↑ · {r.files_skipped} skip · {r.files_failed} fail · {formatBytes(r.bytes_transferred)}
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </button>
              ))}
            </div>
            {runsTotal > PAGE_SIZE && (
              <div className="mt-3 flex items-center justify-between gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-sm gap-1.5"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                  data-testid="runs-prev-page"
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                  {t("common.previous")}
                </Button>
                <span className="font-mono text-[11px] text-muted-foreground">
                  {t("common.page")} {page + 1} / {Math.max(1, Math.ceil(runsTotal / PAGE_SIZE))}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-sm gap-1.5"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={(page + 1) * PAGE_SIZE >= runsTotal}
                  data-testid="runs-next-page"
                >
                  {t("common.next")}
                  <ChevronRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            )}
          </div>

          <div className="lg:col-span-7">
            <h2 className="font-display text-xl font-bold tracking-tight">{t("jobDetail.logsTitle")}</h2>
            {activeRunData ? (
              <div className="mt-4 border border-border bg-[#0d0d10] text-emerald-300">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-white/10 bg-black/40 px-4 py-2 font-mono text-[11px] text-white/70">
                  <span>run::{activeRunData.id.slice(0, 8)}</span>
                  <span>
                    {t("jobDetail.duration")}: {duration(activeRunData.started_at, activeRunData.finished_at)} ·{" "}
                    {t("jobDetail.trigger")}: {activeRunData.trigger}
                  </span>
                </div>
                <div className="max-h-96 overflow-y-auto p-4 font-mono text-xs leading-relaxed">
                  {(!activeRunData.logs || activeRunData.logs.length === 0) && (
                    <div className="text-white/40">{t("jobDetail.noLogs")}</div>
                  )}
                  {activeRunData.logs?.map((l, i) => (
                    <div key={i} className={`flex gap-3 ${l.level === "error" ? "text-red-400" : "text-emerald-300/90"}`}>
                      <span className="shrink-0 text-white/40">
                        {new Date(l.ts).toLocaleTimeString()}
                      </span>
                      <span>{l.message}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="mt-4 border border-border bg-card p-6 font-mono text-sm text-muted-foreground">
                {t("jobDetail.noRuns")}
              </div>
            )}
          </div>
        </section>
      </main>

      <AlertDialog open={confirmDel} onOpenChange={setConfirmDel}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">{t("jobDetail.deleteJob")}</AlertDialogTitle>
            <AlertDialogDescription>{t("jobDetail.confirmDelete")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm" data-testid="cancel-delete-job">{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              className="rounded-sm bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDelete}
              data-testid="confirm-delete-job"
            >
              {t("common.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function AccountLine({ provider, email, fallback }) {
  if (!email) {
    return <div className="truncate text-sm text-muted-foreground">{fallback}</div>;
  }
  return (
    <div className="flex items-center gap-2 text-sm">
      {provider ? <ProviderIcon provider={provider} className="h-4 w-4 shrink-0" /> : null}
      <span className="truncate" title={email}>{email}</span>
    </div>
  );
}

function SummaryCard({ title, body }) {
  return (
    <div className="border border-border bg-card p-5">
      <div className="font-mono text-[11px] uppercase text-muted-foreground">{title}</div>
      <div className="mt-3 space-y-1">{body}</div>
    </div>
  );
}

function Chip({ label }) {
  return (
    <span className="border border-border bg-card px-2 py-0.5 font-mono text-[11px]">{label}</span>
  );
}
